import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert, Autocomplete, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { AlertTriangle, ArrowLeft, Bot, Eraser, CheckCircle2, LayoutTemplate, MessageSquarePlus, Pencil, Redo2, RotateCcw, Send, Sparkles, Wand2, X } from 'lucide-react';

import * as CopilotService from '../../../../../../../../services/2talk/chatbot-flow-copilot.service.js';
import {
    normalizeCopilotAskResult,
    normalizeCopilotHistoryItem,
    normalizeCopilotPromptTemplate,
} from '../../../../../../../../services/2talk/chatbot-flow-copilot.service.js';
import './CopilotPanel.scss';

// Item 09 (16/10/2026) — painel do copiloto de IA. Substitui o Inspector enquanto aberto (coluna do flex do editor).

type CopilotLlm = { id: number; name: string; llmTypeId?: number; model?: string };

type CopilotTemplate = { id: number; code?: string | null; title: string; description?: string; boxesSummary?: string; prompt: string; mode: string; isSystem?: boolean };

type CopilotIssue = { severity: string; nodeKey?: string | null; code: string; message: string };

type CopilotMessage = {
    key: string;
    role: 'user' | 'assistant' | 'info';
    text: string;
    status?: string;
    issues?: CopilotIssue[];
    historyId?: number | null;
    canUndo?: boolean;
    undone?: boolean;
};

type Props = {
    flowId: string;
    llms: CopilotLlm[];
    getBaseJson: () => string;
    /** issues: problemas do fluxo aplicado (applied_with_errors) → Inspector mostra o ícone laranja; ausente = limpa. */
    onApplyJson: (json: string, issues?: CopilotIssue[], fit?: boolean) => void;
    onClose: () => void;
};

const LAST_LLM_STORAGE_KEY = 'mexx.chatbot.copilot.llm';
const PROMPT_MAX_LENGTH = 4000;

const readLastLlm = (): number | null => {
    try {
        const raw = window.localStorage.getItem(LAST_LLM_STORAGE_KEY);
        return raw ? parseInt(raw, 10) : null;
    } catch (e) {
        return null;
    }
};

const writeLastLlm = (id: number | null): void => {
    try {
        if (id) window.localStorage.setItem(LAST_LLM_STORAGE_KEY, String(id));
    } catch (e) {
        // localStorage indisponível (modo privado) — só não lembra a escolha.
    }
};

const errorMessageOf = (err: any, fallback: string): string =>
    err?.response?.data?.errors?.[0] ?? fallback;

const CopilotPanel = ({ flowId, llms, getBaseJson, onApplyJson, onClose }: Props): ReactElement => {
    const intl = useIntl();
    const t = useCallback((id: string, values?: any) => intl.formatMessage({ id }, values), [intl]);

    const [selectedLlm, setSelectedLlm] = useState<CopilotLlm | null>(null);
    const [started, setStarted] = useState(false);
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [messages, setMessages] = useState<CopilotMessage[]>([]);
    const [prompt, setPrompt] = useState('');
    const [sending, setSending] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [expandedIssues, setExpandedIssues] = useState<Record<string, boolean>>({});

    // Item 09b — templates (pedidos prontos).
    const [templates, setTemplates] = useState<CopilotTemplate[]>([]);
    const [view, setView] = useState<'chat' | 'templates'>('chat');
    const [previewTemplate, setPreviewTemplate] = useState<CopilotTemplate | null>(null);
    // Substituir o fluxo do canvas: 'send' = antes de enviar (template "create" ou template editado); 'apply' = a IA devolveu
    // um fluxo novo inteiro (replacesFlow) num pedido livre — só aplica/salva depois do "Substituir".
    const [confirmReplace, setConfirmReplace] = useState<
        { mode: 'send'; title: string; prompt: string } | { mode: 'apply'; result: any; messageKey: string } | null>(null);
    // "Editar antes de enviar" de template create: ao enviar o texto editado, ainda precisa confirmar a substituição.
    const [draftTemplate, setDraftTemplate] = useState<CopilotTemplate | null>(null);

    const listRef = useRef<HTMLDivElement>(null);
    // Trava SÍNCRONA de envio: o estado `sending` só atualiza no próximo render — duplo clique em "Gerar agora"
    // disparava 2 POSTs (2 chamadas à LLM). O ref bloqueia o 2º clique na hora.
    const inFlightRef = useRef(false);

    // LLM inicial: a última usada (se ainda apta) ou a única disponível.
    useEffect(() => {
        if (selectedLlm || llms.length === 0) return;
        const last = readLastLlm();
        const found = llms.find(l => l.id === last) ?? (llms.length === 1 ? llms[0] : null);
        if (found) setSelectedLlm(found);
    }, [llms, selectedLlm]);

    // Retoma a conversa mais recente deste fluxo.
    useEffect(() => {
        let cancelled = false;
        setLoadingHistory(true);
        CopilotService.getHistory(flowId, 50)
            .then((result: any) => {
                if (cancelled || !result?.success) return;
                const rows = (result.data ?? []).map(normalizeCopilotHistoryItem);
                if (rows.length === 0) return;
                const latestConversation = rows[0].conversationId;
                const superseded = new Set(rows.map((r: any) => r.parentHistoryId).filter(Boolean));
                const turns = rows
                    .filter((r: any) => r.conversationId === latestConversation && r.mode !== 'restore' && !superseded.has(r.id))
                    .reverse();
                const restored = new Set(rows.filter((r: any) => r.mode === 'restore').map((r: any) => r.parentHistoryId));
                const loaded: CopilotMessage[] = [];
                turns.forEach((r: any) => {
                    loaded.push({ key: `u-${r.id}`, role: 'user', text: r.userPrompt });
                    loaded.push({
                        key: `a-${r.id}`,
                        role: 'assistant',
                        text: r.assistantMessage || r.errorMessage || t('copilot.message.not.applied'),
                        status: r.status,
                        historyId: r.id,
                        canUndo: r.canRestore && r.status === 'success',
                        undone: restored.has(r.id),
                    });
                });
                setConversationId(latestConversation);
                setMessages(loaded);
                if (loaded.length > 0) setStarted(true);
            })
            .catch(() => { /* histórico é conveniência — painel funciona sem ele */ })
            .finally(() => { if (!cancelled) setLoadingHistory(false); });
        return () => { cancelled = true; };
    }, [flowId, t]);

    useEffect(() => {
        let cancelled = false;
        CopilotService.getTemplates()
            .then((result: any) => {
                if (!cancelled && result?.success) setTemplates((result.data ?? []).map(normalizeCopilotPromptTemplate));
            })
            .catch(() => { /* sem templates: painel funciona com pedidos livres */ });
        return () => { cancelled = true; };
    }, []);

    // Contador do loading local.
    useEffect(() => {
        if (!sending) return;
        setElapsed(0);
        const timer = setInterval(() => setElapsed(s => s + 1), 1000);
        return () => clearInterval(timer);
    }, [sending]);

    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [messages, sending]);

    const suggestions = useMemo(() => [
        t('copilot.suggestion.create.menu'),
        t('copilot.suggestion.add.attendant'),
        t('copilot.suggestion.business.hours'),
        t('copilot.suggestion.review'),
    ], [t]);

    const pushMessage = (message: CopilotMessage) => setMessages(prev => [...prev, message]);

    const handleSelectLlm = (llm: CopilotLlm | null) => {
        if (!llm) return;
        const changed = started && selectedLlm && selectedLlm.id !== llm.id;
        setSelectedLlm(llm);
        writeLastLlm(llm.id);
        if (changed) {
            pushMessage({ key: `i-${Date.now()}`, role: 'info', text: t('copilot.llm.switched', { name: llm.name }) });
        }
    };

    const handleStart = () => {
        if (!selectedLlm) return;
        writeLastLlm(selectedLlm.id);
        setStarted(true);
    };

    const handleNewConversation = () => {
        setConversationId(null);
        setMessages([]);
        setExpandedIssues({});
    };

    const isCanvasEmpty = (): boolean => {
        try {
            return (JSON.parse(getBaseJson())?.cells ?? []).length === 0;
        } catch (e) {
            return true;
        }
    };

    const handleUseTemplate = (template: CopilotTemplate, editFirst: boolean) => {
        setPreviewTemplate(null);
        setView('chat');
        if (editFirst) {
            setPrompt(template.prompt.slice(0, PROMPT_MAX_LENGTH));
            setDraftTemplate(template);
            return;
        }
        if (template.mode !== 'edit' && !isCanvasEmpty()) {
            setConfirmReplace({ mode: 'send', title: template.title, prompt: template.prompt });
            return;
        }
        handleSend(template.prompt, false);
    };

    const handleSend = (text?: string, replace: boolean = false) => {
        const content = (text ?? prompt).trim();
        if (!content || !selectedLlm || sending || inFlightRef.current) return;
        // Texto vindo de "Editar antes de enviar" de um template create: confirma substituir antes (canvas com conteúdo).
        if (text === undefined && draftTemplate) {
            const draft = draftTemplate;
            setDraftTemplate(null);
            if (draft.mode !== 'edit' && !isCanvasEmpty()) {
                setConfirmReplace({ mode: 'send', title: draft.title, prompt: content });
                return;
            }
            replace = draft.mode !== 'edit';
        }
        inFlightRef.current = true;

        setPrompt('');
        pushMessage({ key: `u-${Date.now()}`, role: 'user', text: content });
        setSending(true);

        CopilotService.ask(flowId, {
            prompt: content,
            llmId: selectedLlm.id,
            conversationId,
            baseJson: getBaseJson(),
            replace,
        })
            .then((response: any) => {
                const result = normalizeCopilotAskResult(response?.data);
                if (result.conversationId) setConversationId(result.conversationId);
                // applied_with_errors: nenhuma tentativa passou na validação, mas o fluxo gerado vai pro canvas (NÃO salvo)
                // com os problemas visíveis — o usuário corrige e salva pelo editor.
                const withErrors = result.status === 'applied_with_errors';
                const generated = (result.status === 'success' || withErrors) && !!result.jsonFlow;
                // Fluxo novo inteiro num pedido livre: NÃO aplica nem salva sem o "Substituir" (o backend também não salvou).
                const needsConfirm = generated && result.replacesFlow;
                const applied = generated && !needsConfirm;
                const messageKey = `a-${Date.now()}`;
                if (applied) onApplyJson(result.jsonFlow, withErrors ? result.issues : undefined, replace);
                if (needsConfirm) setConfirmReplace({ mode: 'apply', result, messageKey });
                pushMessage({
                    key: messageKey,
                    role: 'assistant',
                    text: result.assistantMessage || response?.errors?.[0] || t('copilot.message.not.applied'),
                    status: needsConfirm ? 'replace_pending' : (result.status || (response?.success ? 'success' : 'llm_error')),
                    issues: result.issues?.filter((i: CopilotIssue) => i.code !== 'copilot_error'),
                    historyId: result.historyId,
                    canUndo: applied && !!result.historyId,
                });
                if (applied && withErrors) {
                    pushMessage({ key: `i-${Date.now()}`, role: 'info', text: t('copilot.message.applied.with.errors') });
                }
            })
            .catch((err: any) => {
                pushMessage({
                    key: `a-${Date.now()}`,
                    role: 'assistant',
                    text: errorMessageOf(err, t('copilot.error.request')),
                    status: 'llm_error',
                });
            })
            .finally(() => {
                inFlightRef.current = false;
                setSending(false);
            });
    };

    // Confirmou substituir o fluxo pelo que a IA gerou num pedido livre. Sucesso → grava via restore "after" (mesmo
    // endpoint do Refazer: salva o json_after daquela versão). Com problemas → só vai pro canvas (não salva).
    const applyConfirmedReplacement = (result: any, messageKey: string) => {
        const withErrors = result.status === 'applied_with_errors';
        const markApplied = () => setMessages(prev => prev.map(m => m.key === messageKey
            ? { ...m, status: result.status, canUndo: !!result.historyId } : m));
        if (withErrors || !result.historyId) {
            onApplyJson(result.jsonFlow, withErrors ? result.issues : undefined, true);
            markApplied();
            if (withErrors) pushMessage({ key: `i-${Date.now()}`, role: 'info', text: t('copilot.message.applied.with.errors') });
            return;
        }
        inFlightRef.current = true;
        setSending(true);
        CopilotService.restore(flowId, result.historyId, 'after', getBaseJson())
            .then((response: any) => {
                const restored = normalizeCopilotAskResult(response?.data);
                if (response?.success && restored.jsonFlow) {
                    onApplyJson(restored.jsonFlow, undefined, true);
                    markApplied();
                } else {
                    pushMessage({ key: `i-${Date.now()}`, role: 'info', text: response?.errors?.[0] ?? t('copilot.error.restore') });
                }
            })
            .catch((err: any) => pushMessage({ key: `i-${Date.now()}`, role: 'info', text: errorMessageOf(err, t('copilot.error.restore')) }))
            .finally(() => {
                inFlightRef.current = false;
                setSending(false);
            });
    };

    const handleRestore = (message: CopilotMessage, target: 'before' | 'after') => {
        if (!message.historyId || sending || inFlightRef.current) return;
        inFlightRef.current = true;
        setSending(true);
        CopilotService.restore(flowId, message.historyId, target, getBaseJson())
            .then((response: any) => {
                const result = normalizeCopilotAskResult(response?.data);
                if (response?.success && result.jsonFlow) {
                    onApplyJson(result.jsonFlow);
                    setMessages(prev => prev.map(m => m.key === message.key ? { ...m, undone: target === 'before' } : m));
                    pushMessage({
                        key: `i-${Date.now()}`,
                        role: 'info',
                        text: target === 'before' ? t('copilot.undo.done') : t('copilot.redo.done'),
                    });
                } else {
                    pushMessage({ key: `i-${Date.now()}`, role: 'info', text: response?.errors?.[0] ?? t('copilot.error.restore') });
                }
            })
            .catch((err: any) => pushMessage({ key: `i-${Date.now()}`, role: 'info', text: errorMessageOf(err, t('copilot.error.restore')) }))
            .finally(() => {
                inFlightRef.current = false;
                setSending(false);
            });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const renderStatus = (status?: string) => {
        if (!status) return null;
        if (status === 'success') return <Chip size="small" color="success" icon={<CheckCircle2 size={14} />} label={t('copilot.status.applied')} />;
        if (status === 'no_change') return <Chip size="small" variant="outlined" label={t('copilot.status.no.change')} />;
        if (status === 'replace_pending') return <Chip size="small" variant="outlined" color="warning" label={t('copilot.status.replace.pending')} />;
        if (status === 'applied_with_errors') return <Chip size="small" color="warning" icon={<AlertTriangle size={14} />} label={t('copilot.status.applied.with.errors')} />;
        return <Chip size="small" color="warning" icon={<AlertTriangle size={14} />} label={t('copilot.status.not.applied')} />;
    };

    const renderIssues = (message: CopilotMessage) => {
        const issues = message.issues ?? [];
        if (issues.length === 0) return null;
        const errors = issues.filter(i => i.severity === 'error');
        const warnings = issues.filter(i => i.severity !== 'error');
        const open = !!expandedIssues[message.key];
        return (
            <div className="copilot-issues">
                <Button size="small" onClick={() => setExpandedIssues(prev => ({ ...prev, [message.key]: !open }))}>
                    {t('copilot.issues.toggle', { errors: errors.length, warnings: warnings.length })}
                </Button>
                <Collapse in={open}>
                    <ul>
                        {issues.map((issue, index) => (
                            <li key={`${message.key}-${index}`} className={issue.severity === 'error' ? 'issue-error' : 'issue-warning'}>
                                {issue.message}
                            </li>
                        ))}
                    </ul>
                </Collapse>
            </div>
        );
    };

    const llmPicker = (
        <Autocomplete
            size="small"
            options={llms}
            value={selectedLlm}
            disableClearable={!!selectedLlm}
            getOptionLabel={(option: CopilotLlm) => option?.name ?? ''}
            isOptionEqualToValue={(option: CopilotLlm, value: CopilotLlm) => option.id === value.id}
            onChange={(e, value) => handleSelectLlm(value as CopilotLlm | null)}
            renderOption={(props, option: CopilotLlm) => (
                <li {...props} key={option.id}>
                    <div className="copilot-llm-option">
                        <span>{option.name}</span>
                        {option.model && <small>{option.model}</small>}
                    </div>
                </li>
            )}
            renderInput={(params) => <TextField {...params} label={t('copilot.llm.label')} />}
        />
    );

    return (
        <div className="copilot-panel">
            <div className="copilot-header">
                <div className="copilot-title">
                    <Bot size={20} />
                    <span>{t('copilot.title')}</span>
                </div>
                <div className="copilot-header-actions">
                    {started && templates.length > 0 && (
                        <Tooltip title={t('copilot.templates.open')}>
                            <span>
                                <IconButton size="small" onClick={() => setView(view === 'templates' ? 'chat' : 'templates')} disabled={sending}
                                    color={view === 'templates' ? 'primary' : 'default'}>
                                    <LayoutTemplate size={18} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    {started && (
                        <Tooltip title={t('copilot.new.conversation')}>
                            <span>
                                <IconButton size="small" onClick={handleNewConversation} disabled={sending}>
                                    <MessageSquarePlus size={18} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    <Tooltip title={t('copilot.close')}>
                        <IconButton size="small" onClick={onClose}>
                            <X size={18} />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>

            {started && <div className="copilot-llm-bar">{llmPicker}</div>}

            {!started ? (
                <div className="copilot-start">
                    <div className="copilot-start-icon"><Sparkles size={40} /></div>
                    <div className="copilot-start-title">{t('copilot.start.title')}</div>
                    <div className="copilot-start-description">{t('copilot.start.description')}</div>
                    {loadingHistory ? (
                        <CircularProgress size={24} />
                    ) : (
                        <>
                            <div className="copilot-start-picker">{llmPicker}</div>
                            <Button variant="contained" color="primary" disabled={!selectedLlm} onClick={handleStart} fullWidth>
                                {t('copilot.start.button')}
                            </Button>
                        </>
                    )}
                </div>
            ) : view === 'templates' ? (
                <div className="copilot-messages">
                    <div className="copilot-templates-header">
                        <IconButton size="small" onClick={() => setView('chat')}><ArrowLeft size={18} /></IconButton>
                        <span>{t('copilot.templates.choose')}</span>
                    </div>
                    {templates.map(template => (
                        <button type="button" key={template.id} className="copilot-template-card" onClick={() => setPreviewTemplate(template)}>
                            <div className="copilot-template-card-title">
                                <span>{template.title}</span>
                                <Chip size="small" variant="outlined"
                                    label={t(template.mode === 'edit' ? 'copilot.templates.mode.edit' : 'copilot.templates.mode.create')} />
                            </div>
                            {template.description && <div className="copilot-template-card-description">{template.description}</div>}
                            {template.boxesSummary && <div className="copilot-template-card-boxes">{template.boxesSummary}</div>}
                        </button>
                    ))}
                </div>
            ) : (
                <>
                    <div className="copilot-messages" ref={listRef}>
                        {messages.length === 0 && (
                            <div className="copilot-empty">
                                <div className="copilot-empty-title">{t('copilot.empty.title')}</div>
                                {templates.length > 0 ? (
                                    <>
                                        <div className="copilot-empty-subtitle">{t('copilot.templates.featured')}</div>
                                        {templates.slice(0, 4).map(template => (
                                            <button type="button" key={template.id} className="copilot-template-card" onClick={() => setPreviewTemplate(template)}>
                                                <div className="copilot-template-card-title"><span>{template.title}</span></div>
                                                {template.description && <div className="copilot-template-card-description">{template.description}</div>}
                                            </button>
                                        ))}
                                        {templates.length > 4 && (
                                            <Button size="small" onClick={() => setView('templates')}>{t('copilot.templates.see.all', { count: templates.length })}</Button>
                                        )}
                                    </>
                                ) : (
                                    <div className="copilot-suggestions">
                                        {suggestions.map(s => (
                                            <Chip key={s} label={s} variant="outlined" onClick={() => handleSend(s)} disabled={sending} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {messages.map(message => (
                            <div key={message.key} className={`copilot-message copilot-message-${message.role}`}>
                                {message.role === 'assistant' && (
                                    <div className="copilot-message-meta">{renderStatus(message.status)}</div>
                                )}
                                <div className="copilot-bubble">{message.text}</div>
                                {message.role === 'assistant' && renderIssues(message)}
                                {message.role === 'assistant' && message.canUndo && (
                                    <div className="copilot-message-actions">
                                        {!message.undone ? (
                                            <Button size="small" startIcon={<RotateCcw size={14} />} disabled={sending}
                                                onClick={() => handleRestore(message, 'before')}>
                                                {t('copilot.undo')}
                                            </Button>
                                        ) : (
                                            <Button size="small" startIcon={<Redo2 size={14} />} disabled={sending}
                                                onClick={() => handleRestore(message, 'after')}>
                                                {t('copilot.redo')}
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}

                        {sending && (
                            <div className="copilot-message copilot-message-assistant">
                                <div className="copilot-bubble copilot-typing">
                                    <CircularProgress size={16} />
                                    <span>{t('copilot.sending', { seconds: elapsed })}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="copilot-footer">
                        {CopilotService.COPILOT_USE_MOCK && (
                            <Alert severity="info" className="copilot-mock-alert">{t('copilot.mock.notice')}</Alert>
                        )}
                        <TextField
                            multiline
                            minRows={2}
                            maxRows={6}
                            fullWidth
                            value={prompt}
                            disabled={sending || !selectedLlm}
                            placeholder={t('copilot.input.placeholder')}
                            onChange={e => setPrompt(e.target.value.slice(0, PROMPT_MAX_LENGTH))}
                            onKeyDown={handleKeyDown}
                            helperText={t('copilot.input.helper')}
                            InputProps={{
                                endAdornment: prompt.length > 0 && !sending ? (
                                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                        <Tooltip title={t('copilot.input.clear')}>
                                            <IconButton size="small" edge="end" aria-label={t('copilot.input.clear')} onClick={() => { setPrompt(''); setDraftTemplate(null); }}>
                                                <Eraser size={16} />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ) : undefined,
                            }}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            endIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Send size={16} />}
                            disabled={sending || !prompt.trim() || !selectedLlm}
                            onClick={() => handleSend()}
                            fullWidth
                        >
                            {t('copilot.send')}
                        </Button>
                    </div>
                </>
            )}

            {/* Prévia do template */}
            <Dialog open={!!previewTemplate} onClose={() => setPreviewTemplate(null)} fullWidth maxWidth="sm"
                PaperProps={{ sx: { maxHeight: '90vh', borderRadius: 3 } }}>
                <DialogTitle>{previewTemplate?.title}</DialogTitle>
                <DialogContent dividers sx={{ p: 0 }}>
                    <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '16px 20px' }}>
                        {previewTemplate?.description && <p className="copilot-preview-description">{previewTemplate.description}</p>}
                        {previewTemplate?.boxesSummary && (
                            <p className="copilot-preview-boxes"><strong>{t('copilot.templates.boxes')}:</strong> {previewTemplate.boxesSummary}</p>
                        )}
                        <div className="copilot-preview-label">{t('copilot.templates.prompt.label')}</div>
                        <div className="copilot-preview-prompt">{previewTemplate?.prompt}</div>
                        {previewTemplate?.mode !== 'edit' && (
                            <Alert severity="info" sx={{ mt: 2 }}>{t('copilot.templates.create.notice')}</Alert>
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button startIcon={<Pencil size={14} />} onClick={() => previewTemplate && handleUseTemplate(previewTemplate, true)}>
                        {t('copilot.templates.edit.before')}
                    </Button>
                    <Button variant="contained" startIcon={<Wand2 size={14} />} disabled={sending || !selectedLlm}
                        onClick={() => previewTemplate && handleUseTemplate(previewTemplate, false)}>
                        {t('copilot.templates.generate')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Template "create" sobre canvas com conteúdo: confirma substituir (desfazer devolve o original) */}
            <Dialog open={!!confirmReplace} onClose={() => {
                if (confirmReplace?.mode === 'apply') pushMessage({ key: `i-${Date.now()}`, role: 'info', text: t('copilot.replace.kept') });
                setConfirmReplace(null);
            }} maxWidth="xs" fullWidth>
                <DialogTitle>{t('copilot.templates.replace.title')}</DialogTitle>
                <DialogContent>
                    <p style={{ fontSize: 14, margin: 0 }}>
                        {confirmReplace?.mode === 'apply'
                            ? t('copilot.replace.generated.confirm')
                            : t('copilot.templates.replace.confirm', { title: confirmReplace?.mode === 'send' ? confirmReplace.title : '' })}
                    </p>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        if (confirmReplace?.mode === 'apply') pushMessage({ key: `i-${Date.now()}`, role: 'info', text: t('copilot.replace.kept') });
                        setConfirmReplace(null);
                    }}>{t('cancel')}</Button>
                    <Button variant="contained" onClick={() => {
                        const pending = confirmReplace;
                        setConfirmReplace(null);
                        if (pending?.mode === 'send') handleSend(pending.prompt, true);
                        else if (pending?.mode === 'apply') applyConfirmedReplacement(pending.result, pending.messageKey);
                    }}>
                        {t('copilot.templates.replace.button')}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default CopilotPanel;
